import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Brain, Home, Users, Trophy, Heart, MessageCircle, ExternalLink, ArrowLeft, TrendingUp, Loader2, Send, Flag, Pin, Lock, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import FloatingImpactLabButton from '../components/FloatingImpactLabButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';


// Discussions Tab Component
const DiscussionsTab = ({ challengeId, discussions, onRefresh, loading }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showNewDiscussion, setShowNewDiscussion] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [newDiscussion, setNewDiscussion] = useState({
    title: '',
    content: '',
    category: 'general',
    is_private: false
  });
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { value: 'general', label: '💬 General', color: 'slate' },
    { value: 'ideas', label: '💡 Ideas & Concepts', color: 'yellow' },
    { value: 'collaboration', label: '🤝 Looking for Collaborators', color: 'blue' },
    { value: 'brainstorming', label: '🧠 Brainstorming', color: 'purple' },
    { value: 'questions', label: '❓ Questions & Help', color: 'green' },
    { value: 'success', label: '🎉 Success Stories', color: 'pink' }
  ];

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to start a discussion",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/challenges/${challengeId}/discussions`,
        newDiscussion,
        { withCredentials: true }
      );

      toast({
        title: "Success!",
        description: "Discussion created successfully"
      });

      setNewDiscussion({ title: '', content: '', category: 'general', is_private: false });
      setShowNewDiscussion(false);
      onRefresh();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create discussion",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (selectedDiscussion) {
    return (
      <DiscussionDetail
        discussion={selectedDiscussion}
        onBack={() => setSelectedDiscussion(null)}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Create Discussion Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold">Challenge Discussions</h3>
        <Button
          onClick={() => {
            if (!user) {
              navigate('/auth');
              return;
            }
            setShowNewDiscussion(!showNewDiscussion);
          }}
          className="bg-gradient-to-r from-purple-600 to-pink-600"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          New Discussion
        </Button>
      </div>

      {/* New Discussion Form */}
      {showNewDiscussion && (
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle>Start a New Discussion</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateDiscussion} className="space-y-4">
              <div>
                <label className="text-sm font-semibold">Title</label>
                <input
                  type="text"
                  value={newDiscussion.title}
                  onChange={(e) => setNewDiscussion({ ...newDiscussion, title: e.target.value })}
                  className="w-full mt-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="What would you like to discuss?"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Category</label>
                <select
                  value={newDiscussion.category}
                  onChange={(e) => setNewDiscussion({ ...newDiscussion, category: e.target.value })}
                  className="w-full mt-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold">Description</label>
                <textarea
                  value={newDiscussion.content}
                  onChange={(e) => setNewDiscussion({ ...newDiscussion, content: e.target.value })}
                  className="w-full mt-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Share your thoughts, ideas, or questions..."
                  rows={5}
                  required
                />
              </div>

              {user && user.team_id && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="private"
                    checked={newDiscussion.is_private}
                    onChange={(e) => setNewDiscussion({ ...newDiscussion, is_private: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="private" className="text-sm">
                    <Lock className="w-4 h-4 inline mr-1" />
                    Make this discussion private to my team
                  </label>
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="bg-gradient-to-r from-purple-600 to-pink-600">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Discussion'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowNewDiscussion(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Discussion List */}
      {discussions.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No discussions yet. Be the first to start one!</p>
            <Button onClick={() => setShowNewDiscussion(true)} className="bg-gradient-to-r from-purple-600 to-pink-600">
              Start Discussion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => {
            const category = categories.find(c => c.value === discussion.category) || categories[0];
            return (
              <Card
                key={discussion.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedDiscussion(discussion)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {discussion.is_pinned && (
                          <Pin className="w-4 h-4 text-purple-600" />
                        )}
                        {discussion.is_private && (
                          <Lock className="w-4 h-4 text-blue-600" />
                        )}
                        <Badge className={`bg-${category.color}-100 text-${category.color}-700 border-0`}>
                          {category.label}
                        </Badge>
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 mb-2">{discussion.title}</h4>
                      <p className="text-slate-600 text-sm line-clamp-2 mb-3">{discussion.content}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span>by {discussion.author_name}</span>
                        <span>•</span>
                        <span>{discussion.comment_count} comments</span>
                        <span>•</span>
                        <span>{discussion.views} views</span>
                        <span>•</span>
                        <span>{new Date(discussion.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Discussion Detail Component
const DiscussionDetail = ({ discussion, onBack, onRefresh }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDiscussionDetail();
  }, [discussion.id]);

  const fetchDiscussionDetail = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/discussions/${discussion.id}`, {
        withCredentials: true
      });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching discussion:', error);
      toast({
        title: "Error",
        description: "Failed to load discussion details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePostComment = async (e) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to comment",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    if (!newComment.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/discussions/${discussion.id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );

      toast({
        title: "Success!",
        description: "Comment posted"
      });

      setNewComment('');
      fetchDiscussionDetail();
      onRefresh();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to post comment",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/comments/${commentId}`, {
        withCredentials: true
      });

      toast({
        title: "Success!",
        description: "Comment deleted"
      });

      fetchDiscussionDetail();
      onRefresh();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete comment",
        variant: "destructive"
      });
    }
  };

  const handleReport = async (targetType, targetId) => {
    const reason = prompt('Please provide a reason for reporting this content:');
    if (!reason) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/report`,
        { target_type: targetType, target_id: targetId, reason },
        { withCredentials: true }
      );

      toast({
        title: "Reported",
        description: "Thank you. Moderators will review this content."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to report",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <Button onClick={onBack} variant="outline">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Discussions
      </Button>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <CardTitle className="text-2xl mb-2">{discussion.title}</CardTitle>
              <CardDescription>
                by {discussion.author_name} • {new Date(discussion.created_at).toLocaleDateString()}
              </CardDescription>
            </div>
            {user && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleReport('discussion', discussion.id)}
              >
                <Flag className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700 whitespace-pre-wrap">{discussion.content}</p>
        </CardContent>
      </Card>

      {/* Comments */}
      <div>
        <h3 className="text-xl font-bold mb-4">{comments.length} Comments</h3>

        {/* New Comment Form */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handlePostComment}>
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={user ? "Share your thoughts..." : "Please login to comment"}
                rows={3}
                disabled={!user}
              />
              <div className="flex justify-end mt-3">
                <Button
                  type="submit"
                  disabled={submitting || !user}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Post Comment
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Comments List */}
        <div className="space-y-4">
          {comments.map((comment) => (
            <Card key={comment.id}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="font-semibold">
                      {comment.author_name}
                    </span>
                    <span className="text-sm text-slate-500 ml-3">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {user && comment.author_id === user.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                    {user && comment.author_id !== user.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleReport('comment', comment.id)}
                      >
                        <Flag className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{comment.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const ChallengeDetail = () => {
  const navigate = useNavigate();
  const { challengeId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [sortBy, setSortBy] = useState('oi-score');
  const [challenge, setChallenge] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discussionsLoading, setDiscussionsLoading] = useState(false);

  useEffect(() => {
    fetchChallengeData();
    fetchDiscussions();
  }, [challengeId]);

  const fetchChallengeData = async () => {
    try {
      setLoading(true);
      
      const challengeRes = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}`, {
        withCredentials: true
      });
      
      setChallenge(challengeRes.data);
      
      const submissionsRes = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}/submissions`, {
        withCredentials: true
      });
      setSubmissions(submissionsRes.data);
    } catch (error) {
      console.error('Error fetching challenge data:', error);
      console.error('Challenge ID attempted:', challengeId);
      console.error('Error response:', error.response?.data);
      
      // Better error handling - don't show "not found" for network errors
      if (error.response?.status === 404) {
        setChallenge({
          id: challengeId,
          title: 'Challenge Not Found',
          sdg: 13,
          sdg_name: 'Error',
          description: `Could not find challenge with ID: ${challengeId}`,
          full_description: 'Please check the challenge ID or return to the challenges page. The challenge may have been removed or the link may be incorrect.',
          participants: 0,
          submissions: 0,
          deadline: '2025-09-30',
          difficulty: 'Advanced',
          color: 'from-teal-500 to-green-500',
          tags: [],
          criteria: []
        });
      } else {
        // For other errors, try to load from local data or show generic error
        
        setChallenge({
          id: challengeId,
          title: 'Loading Challenge...',
          sdg: 13,
          sdg_name: 'Loading...',
          description: 'Please wait while we load the challenge details.',
          full_description: '',
          participants: 0,
          submissions: 0,
          deadline: '2025-09-30',
          difficulty: 'Advanced',
          color: 'from-teal-500 to-green-500',
          tags: [],
          criteria: []
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscussions = async () => {
    try {
      setDiscussionsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/challenges/${challengeId}/discussions`, {
        withCredentials: true
      });
      setDiscussions(response.data.discussions || []);
    } catch (error) {
      console.error('Error fetching discussions:', error);
      setDiscussions([]);
    } finally {
      setDiscussionsLoading(false);
    }
  };


  const handleLike = async (submissionId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/submissions/${submissionId}/like`);
      setSubmissions(submissions.map(s => 
        s.id === submissionId ? { ...s, likes: s.likes + 1 } : s
      ));
    } catch (error) {
      console.error('Error liking submission:', error);
    }
  };

  const getSortedSubmissions = () => {
    const sorted = [...submissions];
    if (sortBy === 'oi-score') {
      return sorted.sort((a, b) => b.oi_score - a.oi_score);
    } else if (sortBy === 'spark') {
      return sorted.sort((a, b) => b.spark_pct - a.spark_pct);
    } else if (sortBy === 'popular') {
      return sorted.sort((a, b) => b.likes - a.likes);
    } else if (sortBy === 'recent') {
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return sorted;
  };

  if (loading || !challenge) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-purple-600 mb-4" />
          <p className="text-slate-600">Loading challenge...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              Idea Fountain
            </h1>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/challenges')}
              className="border-purple-200 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Challenges
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="border-purple-200 hover:bg-purple-50"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Challenge Header */}
          <Card className="mb-8 border-2">
            <div className={`h-3 bg-gradient-to-r ${challenge.color}`}></div>
            <CardHeader>
              <Badge className={`w-fit bg-gradient-to-r ${challenge.color} text-white border-0 mb-3`}>
                SDG {challenge.sdg}: {challenge.sdg_name}
              </Badge>
              <CardTitle className="text-3xl mb-2">{challenge.title}</CardTitle>
              <CardDescription className="text-base">{challenge.full_description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">Participants</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{challenge.participants}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm">Public Submissions</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{challenge.submissions}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <span className="text-sm">Deadline</span>
                  </div>
                  <div className="text-lg font-bold text-slate-900">{new Date(challenge.deadline).toLocaleDateString()}</div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Evaluation Criteria:</h4>
                <div className="grid md:grid-cols-2 gap-2">
                  {challenge.criteria && challenge.criteria.map((criterion, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      {criterion}
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                size="lg"
                className={`w-full bg-gradient-to-r ${challenge.color}`}
                onClick={() => navigate('/session/new?persona=social-innovation&challenge=' + challenge.id)}
              >
                Submit Your Solution
              </Button>
            </CardContent>
          </Card>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'submissions'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Submissions ({submissions.length})
            </button>
            <button
              onClick={() => setActiveTab('discussions')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'discussions'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              💬 Discussions ({discussions.length})
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="bg-white p-6 rounded-lg border">
              <h3 className="text-xl font-bold mb-4">About This Challenge</h3>
              <p className="text-slate-700 whitespace-pre-wrap">{challenge.full_description}</p>
            </div>
          )}

          {/* Submissions Tab */}
          {activeTab === 'submissions' && (
            <>
              {/* Submissions Section */}
              <div className="mb-6 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-slate-900">Public Submissions ({submissions.length})</h3>
                <div className="flex gap-2">
                  <Button
                    variant={sortBy === 'oi-score' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSortBy('oi-score')}
                  >
                    Top OI Score
                  </Button>
                  <Button
                    variant={sortBy === 'spark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSortBy('spark')}
                  >
                    Top Spark
                  </Button>
                  <Button
                    variant={sortBy === 'popular' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSortBy('popular')}
                  >
                    Most Popular
                  </Button>
                  <Button
                    variant={sortBy === 'recent' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSortBy('recent')}
                  >
                    Recent
                  </Button>
                </div>
              </div>

              {/* Submission Cards */}
              <div className="space-y-6">
                {getSortedSubmissions().map((submission, index) => (
                  <Card key={submission.id} className="hover:shadow-xl transition-all duration-300 border-2">
                    <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">{submission.country}</div>
                      <div>
                        <CardTitle className="text-xl mb-1">{submission.title}</CardTitle>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <span className="font-medium">{submission.author}</span>
                          <span>•</span>
                          <span>{new Date(submission.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    {index === 0 && sortBy === 'oi-score' && (
                      <Trophy className="w-6 h-6 text-amber-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-700">{submission.summary}</p>
                  
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold text-green-800">Projected Impact:</div>
                        <div className="text-sm text-green-700">{submission.impact}</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <div className="text-xs text-slate-600 mb-1">Originality Index</div>
                      <div className="flex items-center gap-2">
                        <div className="text-2xl font-bold text-purple-700">{submission.oi_score}</div>
                        <Badge className="bg-purple-200 text-purple-800 border-0">{submission.oi_label}</Badge>
                      </div>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <div className="text-xs text-slate-600 mb-1">Spark Level</div>
                      <div className="flex items-center gap-2">
                        <div className="text-2xl font-bold text-emerald-700">{submission.spark_pct}%</div>
                        <Badge className="bg-emerald-200 text-emerald-800 border-0">{submission.spark_label}</Badge>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {submission.tags && submission.tags.map((tag, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex gap-4">
                      <button 
                        className="flex items-center gap-2 text-slate-600 hover:text-red-600 transition-colors"
                        onClick={() => handleLike(submission.id)}
                      >
                        <Heart className="w-5 h-5" />
                        <span className="text-sm font-medium">{submission.likes}</span>
                      </button>
                      <button className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">{submission.comments}</span>
                      </button>
                    </div>
                    <Button variant="outline" size="sm">
                      View Full Solution
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
            </>
          )}

          {/* Discussions Tab */}
          {activeTab === 'discussions' && (
            <DiscussionsTab 
              challengeId={challengeId}
              discussions={discussions}
              onRefresh={fetchDiscussions}
              loading={discussionsLoading}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-gradient-to-r from-purple-50 to-pink-50 mt-16">
        <div className="container mx-auto px-6 py-8 text-center text-slate-700">
          <p className="font-medium">Idea Fountain - Originality Enabling System for the AI Age</p>
          <p className="text-sm text-slate-600 mt-2">
            © 2025 Priyanka Jha | IF™
          </p>
        </div>
      </footer>

      {/* Floating Impact Lab Button */}
      <FloatingImpactLabButton />
    </div>
  );
};

export default ChallengeDetail;
